import { ReadHolidayResponseDto } from '@/api.types';
import { milissegundos_por_dia } from '@/shared/utils/datas';

/** @param mes Mês 0-based (convenção `Date`: janeiro = 0). */
export function diaEmTimestamp(ano: number, mes: number, dia: number): number {
  return new Date(ano, mes, dia).getTime();
}

export function timestampDoDia(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** Mês/dia compacto (MMDD) para comparação anual. @param mes Mês 1-based (janeiro = 1). */
function md(mes: number, dia: number): number {
  return mes * 100 + dia;
}

/** Extrai mês/dia em UTC — alinhado à exibição de datas da API (`DataAmigavel`). */
function mdFromDate(date: Date | string): number {
  const d = typeof date === 'string' ? new Date(date) : date;
  return md(d.getUTCMonth() + 1, d.getUTCDate());
}

/**
 * Verifica se mês/dia cai no intervalo anual do bloqueio.
 * @param mes1 Mês 1-based (janeiro = 1).
 */
function isDiaNoIntervaloAnual(
  mes1: number,
  dia: number,
  inicio: Date | string,
  fim?: Date | string | null,
): boolean {
  const atual = md(mes1, dia);
  const inicioMd = mdFromDate(inicio);
  const fimMd = mdFromDate(fim ?? inicio);

  if (inicioMd <= fimMd) {
    return atual >= inicioMd && atual <= fimMd;
  }

  return atual >= inicioMd || atual <= fimMd;
}

/**
 * Índice dia → feriados para lookup O(1) no calendário anual.
 * Expande intervalos [inicio, fim] dentro do ano informado.
 * Bloqueios com repetição anual ignoram o ano cadastrado.
 */
export function indexarFeriadosPorDia(
  feriados: ReadHolidayResponseDto[],
  ano: number,
): Map<number, ReadHolidayResponseDto[]> {
  const indice = new Map<number, ReadHolidayResponseDto[]>();
  const inicioAno = diaEmTimestamp(ano, 0, 1);
  const fimAno = diaEmTimestamp(ano, 11, 31);

  const adicionar = (ts: number, feriado: ReadHolidayResponseDto) => {
    const lista = indice.get(ts);
    if (lista) lista.push(feriado);
    else indice.set(ts, [feriado]);
  };

  for (const feriado of feriados) {
    if (feriado.repete_anualmente) {
      for (let mesIndex = 0; mesIndex < 12; mesIndex++) {
        const mes1 = mesIndex + 1;
        const diasNoMes = new Date(ano, mesIndex + 1, 0).getDate();

        for (let dia = 1; dia <= diasNoMes; dia++) {
          if (isDiaNoIntervaloAnual(mes1, dia, feriado.inicio, feriado.fim)) {
            adicionar(diaEmTimestamp(ano, mesIndex, dia), feriado);
          }
        }
      }
      continue;
    }

    const inicio = timestampDoDia(new Date(feriado.inicio));
    const fim = feriado.fim ? timestampDoDia(new Date(feriado.fim)) : inicio;

    if (fim < inicioAno || inicio > fimAno) continue;

    for (let ts = Math.max(inicio, inicioAno); ts <= Math.min(fim, fimAno); ts += milissegundos_por_dia) {
      adicionar(ts, feriado);
    }
  }

  return indice;
}

/** Formata período de bloqueio para exibição (dia/mês quando anual). */
export function formatarPeriodoBloqueio(feriado: ReadHolidayResponseDto, dataAmigavel: (d?: string | Date | null) => string): string {
  const formatarDiaMes = (dateStr: string) => {
    const partes = dataAmigavel(dateStr).split('/');
    return partes.length >= 2 ? `${partes[0]}/${partes[1]}` : dataAmigavel(dateStr);
  };

  if (feriado.repete_anualmente) {
    const di = formatarDiaMes(feriado.inicio);
    const df = feriado.fim ? formatarDiaMes(feriado.fim) : null;
    const periodo = df && di !== df ? `${di} até ${df}` : di;
    return `${periodo} (anual)`;
  }

  const di = dataAmigavel(feriado.inicio);
  const df = feriado.fim ? dataAmigavel(feriado.fim) : null;
  return df && di !== df ? `${di} até ${df}` : di;
}
